/**
 * Cached MSAL token — avoids calling acquireTokenSilent on every API request.
 * Tokens last ~1hr; we cache for 5 minutes then refresh.
 */
let cachedToken = null;
let cachedTokenExpiry = 0;
const TOKEN_CACHE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the cached MSAL token so the next `getAccessToken()` call does a
 * fresh acquireTokenSilent. Used by `request()` to recover from a 401 that
 * happened because our cached token was stale or MSAL wasn't ready on the
 * first call (classic race — see commits 9fc1b83, e9d3cfb).
 */
export function invalidateTokenCache() {
  cachedToken = null;
  cachedTokenExpiry = 0;
}

export async function getAccessToken() {
  const now = Date.now();
  if (cachedToken && now < cachedTokenExpiry) return cachedToken;

  try {
    const msal = window.__msalInstance;
    if (!msal) return null;
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return null;

    let result;
    try {
      result = await msal.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0],
      });
    } catch {
      // Silent failed (e.g. expired refresh token) — try popup
      try {
        result = await msal.acquireTokenPopup({
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        });
      } catch {
        return null;
      }
    }
    cachedToken = result?.idToken || null;
    cachedTokenExpiry = now + TOKEN_CACHE_MS;
    return cachedToken;
  } catch {
    cachedToken = null;
    return null;
  }
}

/**
 * Thin wrapper around fetch for all API calls.
 * Attaches MSAL Bearer token for authentication.
 *
 * Automatically retries once on 401 (Unauthenticated) after forcing a fresh
 * token — handles the MSAL-not-ready-yet race on initial page load without
 * every page having to implement its own retry (same pattern useCurrentUser
 * already uses internally). This fix has been reintroduced multiple times
 * (commits 4f31c9c, 532f3c1, e9d3cfb, 9fc1b83) — please don't remove it.
 */
async function request(method, path, body, _isRetry = false) {
  const token = await getAccessToken();
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  };
  if (token) {
    opts.headers['Authorization'] = `Bearer ${token}`;
    opts.headers['X-ProHR-Token'] = `Bearer ${token}`;
  }
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(path, opts);

  // Retry once on 401: clear the cached token (might be stale or MSAL wasn't
  // ready when we grabbed it) and try again. Wait 500ms so MSAL has a beat
  // to finish its own init.
  if (res.status === 401 && !_isRetry) {
    invalidateTokenCache();
    await new Promise((r) => setTimeout(r, 500));
    return request(method, path, body, true);
  }

  let payload = null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    payload = await res.json().catch(() => null);
  } else if (res.status !== 204) {
    payload = await res.text().catch(() => null);
  }

  if (!res.ok) {
    let message =
      (payload && payload.error) ||
      (typeof payload === 'string' ? payload : null) ||
      `Request failed: ${res.status} ${res.statusText}`;
    // A 401 that survived the auto-retry above (which already tried a
    // fresh token) almost always means the sign-in session went stale.
    // Replace the bare "Unauthenticated" with the fix that actually
    // works, so users aren't left staring at a cryptic message.
    if (res.status === 401) {
      message = 'Your session has expired. Please sign out and back in, then try again.';
      // Signal the UI to show a persistent "session expired" banner. Fires
      // only after the auto-retry above already tried a fresh token, so this
      // is a genuinely dead session — not a transient race. AppStatusBanner
      // listens; it never force-logs-out (no data loss), just prompts.
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('prohr:session-expired'));
      }
    }
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

/**
 * Multipart upload with Bearer token auth.
 *
 * Pass `opts.onProgress(percent)` to receive live upload progress
 * (0-100). When supplied, the call uses XHR under the hood so the
 * browser can fire upload-progress events — fetch() doesn't expose
 * those. Without an onProgress callback we keep using fetch (lighter
 * code path, no behaviour change for existing callers).
 *
 * `opts.method` defaults to POST.
 */
async function uploadWithAuth(url, formData, opts = {}, _isRetry = false) {
  // Back-compat: older callers pass `method` as a string instead of opts.
  if (typeof opts === 'string') opts = { method: opts };
  const { method = 'POST', onProgress } = opts;

  const token = await getAccessToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['X-ProHR-Token'] = `Bearer ${token}`;
  }

  // XHR path: only when caller wants progress events.
  if (typeof onProgress === 'function') {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.withCredentials = true;
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = async () => {
        if (xhr.status === 401 && !_isRetry) {
          invalidateTokenCache();
          await new Promise((r) => setTimeout(r, 500));
          try { resolve(await uploadWithAuth(url, formData, opts, true)); }
          catch (err) { reject(err); }
          return;
        }
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch {}
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data?.error || `Upload failed: ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.ontimeout = () => reject(new Error('Upload failed: timed out'));
      xhr.send(formData);
    });
  }

  // fetch path (no progress).
  const res = await fetch(url, { method, body: formData, headers, credentials: 'include' });
  if (res.status === 401 && !_isRetry) {
    invalidateTokenCache();
    await new Promise((r) => setTimeout(r, 500));
    return uploadWithAuth(url, formData, opts, true);
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Upload failed: ${res.status}`);
  return data;
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  patch: (path, body) => request('PATCH', path, body),
  del: (path) => request('DELETE', path),
};

// Typed helpers per resource
export const adminApi = {
  // Users
  listUsers: () => api.get('/api/mgmt/users'),
  updateUser: (userId, data) => api.put(`/api/mgmt/users/${userId}`, data),

  // Configurable roles & section permissions.
  listRoles: () => api.get('/api/mgmt/roles'),
  upsertRole: (data) => api.post('/api/mgmt/roles', data),
  deleteRole: (key) => api.del(`/api/mgmt/roles/${encodeURIComponent(key)}`),

  // Visas (HR/admin)
  listVisas: () => api.get('/api/mgmt/visas'),
  fulfillVisa: (userId, id, formData) => uploadWithAuth(`/api/mgmt/visas/${encodeURIComponent(userId)}/${encodeURIComponent(id)}/fulfill`, formData),
  updateVisa: (userId, id, data) => api.post(`/api/mgmt/visas/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`, data),
  deleteVisa: (userId, id) => api.del(`/api/mgmt/visas/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`),
  // Restart a visa's chain: { resnapshot:true } rebuilds from the configured
  // chain (re-route to a new chain); { fromStep:i } resets existing steps.
  restartVisaChain: (userId, id, body) => api.post(`/api/mgmt/visas/${encodeURIComponent(userId)}/${encodeURIComponent(id)}/restart-chain`, body || {}),
  syncUsers: () => api.post('/api/mgmt/users/sync', {}),
  searchGraphUsers: (q) => api.get(`/api/mgmt/graph/users/search?q=${encodeURIComponent(q)}`),
  inviteUser: (data) => api.post('/api/mgmt/users/invite', data),
  deleteUser: (userId) => api.del(`/api/mgmt/users/${userId}`),
  bulkDeleteUsers: (userIds) => api.post('/api/mgmt/users/bulk-delete', { userIds }),

  // Team calendar subscribe URL
  getTeamCalendarUrl: () => api.get('/api/mgmt/calendar-url'),
  rotateTeamCalendarUrl: () => api.post('/api/mgmt/calendar-url', { rotate: true }),

  // Leave types
  listLeaveTypes: () => api.get('/api/mgmt/leave-types'),
  upsertLeaveType: (typeId, data) =>
    api.post(`/api/mgmt/leave-types/${typeId}`, data),
  deleteLeaveType: (typeId) => api.del(`/api/mgmt/leave-types/${typeId}`),
  hardDeleteLeaveType: (typeId, force = false) =>
    api.del(`/api/mgmt/leave-types/${typeId}?hard=true${force ? '&force=true' : ''}`),

  // Holidays
  listHolidays: (year) =>
    api.get(year ? `/api/mgmt/holidays/${year}` : '/api/mgmt/holidays'),
  upsertHoliday: (data) => api.post('/api/mgmt/holidays', data),
  deleteHoliday: (year, id) => api.del(`/api/mgmt/holidays/${year}/${id}`),
  importHolidays: (pack) => api.post('/api/mgmt/holidays/import', { pack }),
  // Holiday duplicate cleanup. GET previews the groups that would be
  // removed; POST { confirm: true } actually deletes the older
  // copies, keeping the oldest row per (date, name, country) tuple.
  previewHolidayDupes: () => api.get('/api/mgmt/holidays/dedupe'),
  runHolidayDedupe: () => api.post('/api/mgmt/holidays/dedupe', { confirm: true }),
  // Shift KSA holidays that landed on Fri/Sat onto the next working
  // day (Sun → Mon → …). Original weekend row is deleted, observed
  // row is created on the target date.
  previewWeekendShift: () => api.get('/api/mgmt/holidays/shift-weekend'),
  runWeekendShift: () => api.post('/api/mgmt/holidays/shift-weekend', { confirm: true }),
  // Bulk-create Outlook events for any approved leave / WFH /
  // Remote rows that are missing them. Rows that already have
  // event IDs are left alone (no double-booking).
  previewCalendarResync: () => api.get('/api/mgmt/calendar-resync'),
  runCalendarResync: () => api.post('/api/mgmt/calendar-resync', { confirm: true }),

  // Approval chains
  listChains: () => api.get('/api/mgmt/chains'),
  upsertChain: (data) => api.post('/api/mgmt/chains', data),
  deleteChain: (rowKey) => api.del(`/api/mgmt/chains/${rowKey}`),

  // Balances
  listBalances: (year) =>
    api.get(year ? `/api/mgmt/balances/${year}` : '/api/mgmt/balances'),
  // Per-user balances using the EXACT same logic as my-balances. The
  // admin grid calls this once per visible user so each cell is
  // computed by the same code that powers the employee dashboard.
  getUserBalances: (userId, year) => {
    const t = Date.now();
    return api.get(`/api/mgmt/user-balances/${encodeURIComponent(userId)}/${year}?_=${t}`);
  },
  adjustBalance: (data) => api.post('/api/mgmt/balances/adjust', data),
  // Drift check: what the stored used/pending counters say vs what the leave
  // requests actually add up to. userId also returns the requests behind it.
  auditBalances: (year, userId) =>
    api.get(`/api/mgmt/balance-audit/${year}${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  reconcileBalance: (data) => api.post('/api/mgmt/balance-audit/reconcile', data),
  bulkGrant: (data) => api.post('/api/mgmt/balances/bulk-grant', data),

  // Settings
  getSettings: () => api.get('/api/mgmt/settings'),
  updateSettings: (data) => api.put('/api/mgmt/settings', data),

  // Email templates
  listTemplates: () => api.get('/api/mgmt/templates'),
  upsertTemplate: (templateId, data) =>
    api.post(`/api/mgmt/templates/${templateId}`, data),

  // Audit log
  listAudit: (yearMonth) =>
    api.get(yearMonth ? `/api/mgmt/audit/${yearMonth}` : '/api/mgmt/audit'),

  // Seed
  seed: (force = false) => api.post('/api/mgmt/seed', { force }),

  // Reports
  getReport: (type, year, department) => {
    const params = new URLSearchParams({ type });
    if (year) params.set('year', year);
    if (department) params.set('department', department);
    return api.get(`/api/mgmt/reports?${params}`);
  },

  // Year-end rollover
  rollover: (fromYear, dryRun = true) =>
    api.post('/api/mgmt/rollover', { fromYear, dryRun }),

  // Health check
  getHealthCheck: () => api.get('/api/mgmt/health'),

  // Leave requests (admin)
  listLeaveRequests: () => api.get('/api/mgmt/leave-requests'),
  updateLeaveRequest: (userId, requestId, updates) =>
    api.post(`/api/mgmt/leave-requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}`, { updates }),
  deleteLeaveRequest: (userId, requestId) =>
    api.del(`/api/mgmt/leave-requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}`),
  restartLeaveChain: (userId, requestId, fromStep = 0) =>
    api.post(`/api/mgmt/leave-requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}/restart-chain`, { fromStep }),

  // Calendar integration
  listGroups: () => api.get('/api/mgmt/groups'),
  syncHolidaysToCalendar: () => api.post('/api/mgmt/holidays/sync-calendar', {}),

  // Teams
  listTeams: () => api.get('/api/mgmt/teams'),
  upsertTeam: (teamId, data) => api.post(`/api/mgmt/teams/${teamId}`, data),
  deleteTeam: (teamId) => api.del(`/api/mgmt/teams/${teamId}`),

  // Travel
  listTravelRequests: () => api.get('/api/mgmt/travel'),
  // Accepts either a plain object (JSON) or a FormData with an optional
  // `bookingTicket` PDF file. The backend dispatches on Content-Type.
  updateTravelBooking: (userId, requestId, data) => {
    const url = `/api/mgmt/travel/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}`;
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return uploadWithAuth(url, data);
    }
    return api.post(url, data);
  },
  cancelTravel: (userId, requestId) =>
    api.post(`/api/mgmt/travel/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}/cancel`, {}),

  // Expense export template
  uploadExpenseTemplate: (formData) => uploadWithAuth('/api/mgmt/expense-template', formData),
  getExpenseTemplateMapping: () => api.get('/api/mgmt/expense-template/mapping'),
  saveExpenseTemplateMapping: (data) => api.post('/api/mgmt/expense-template/mapping', data),
  exportExpenseReport: async (reportId) => {
    const token = await getAccessToken();
    const headers = {};
    if (token) { headers['Authorization'] = `Bearer ${token}`; headers['X-ProHR-Token'] = `Bearer ${token}`; }
    const res = await fetch(`/api/mgmt/expense-reports/${encodeURIComponent(reportId)}/export`, { headers, credentials: 'include' });
    if (!res.ok) { const d = await res.json().catch(() => null); throw new Error(d?.error || `Export failed: ${res.status}`); }
    return res.blob();
  },

  // Leave export template
  uploadLeaveTemplate: (formData) => uploadWithAuth('/api/mgmt/leave-template', formData),
  getLeaveTemplateMapping: () => api.get('/api/mgmt/leave-template/mapping'),
  saveLeaveTemplateMapping: (data) => api.post('/api/mgmt/leave-template/mapping', data),
  exportLeaveRequest: async (userId, requestId) => {
    const token = await getAccessToken();
    const headers = {};
    if (token) { headers['Authorization'] = `Bearer ${token}`; headers['X-ProHR-Token'] = `Bearer ${token}`; }
    const res = await fetch(`/api/mgmt/leave-requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}/export`, { headers, credentials: 'include' });
    if (!res.ok) { const d = await res.json().catch(() => null); throw new Error(d?.error || `Export failed: ${res.status}`); }
    return res.blob();
  },

  // Document Types
  listDocTypes: () => api.get('/api/mgmt/document-types'),
  upsertDocType: (typeId, data) => api.post(`/api/mgmt/document-types/${typeId}`, data),
  deleteDocType: (typeId) => api.del(`/api/mgmt/document-types/${typeId}`),

  // Documents
  listDocuments: () => api.get('/api/mgmt/documents'),
  uploadDocument: (userId, docTypeId, formData, opts) =>
    uploadWithAuth(`/api/mgmt/documents/${userId}/${docTypeId}`, formData, opts),
  deleteDocument: (userId, docTypeId) => api.del(`/api/mgmt/documents/${userId}/${docTypeId}`),

  // Contracts
  uploadContract: (userId, formData) =>
    uploadWithAuth(`/api/mgmt/contracts/${userId}`, formData),
  deleteContract: (userId) => api.del(`/api/mgmt/contracts/${userId}`),
  getExpiringContracts: () => api.get('/api/mgmt/contracts/expiring'),

  // Expiry reminders — manual "send now" for the Contracts and
  // Documents pages. Body { kinds?: ['contracts','documents'] }.
  sendExpiryReminders: (kinds) => api.post('/api/mgmt/expiry-reminders/send', { kinds }),
};

// Employee helpers
export const employeeApi = {
  getProfile: () => api.get('/api/my/profile'),
  // Store the browser's detected IANA timezone so the daily approver digest
  // can fire at ~9am in the user's own local time.
  setTimezone: (timeZone) => api.post('/api/my/timezone', { timeZone }),
  getBalances: () => api.get('/api/my/balances'),
  listRequests: () => api.get('/api/my/requests'),
  getRequest: (id) => api.get(`/api/my/requests/${encodeURIComponent(id)}`),
  submitRequest: (data) => api.post('/api/my/requests', data),
  // One consolidated confirmation email for a planner checkout that submitted
  // several requests at once (each submit passes suppressAck to skip its own).
  submitBatchAck: (summary) => api.post('/api/my/requests/batch-ack', summary),
  // Edit a pending leave request — resets the approval chain and re-notifies
  // the first approver. PUT so it updates the existing row in place.
  updateRequest: (id, data) => api.put(`/api/my/requests/${encodeURIComponent(id)}`, data),
  cancelRequest: (id) => api.post(`/api/my/requests/${encodeURIComponent(id)}/cancel`),
  getLeaveTypes: () => api.get('/api/shared/leave-types'),
  getHolidays: (year) => api.get(`/api/shared/holidays/${year}`),
  getGroupCalendar: (startDate, endDate) =>
    api.get(`/api/shared/group-calendar?startDate=${startDate}&endDate=${endDate}`),
  getTeamCalendar: (startDate, endDate) =>
    api.get(`/api/shared/team-calendar?startDate=${startDate}&endDate=${endDate}`),
  getMyContract: () => api.get('/api/my/contract'),
  getMyDocuments: () => api.get('/api/my/documents'),
  // Visas (employee self-service)
  submitVisa: (data) => api.post('/api/my/visas', data),
  listMyVisas: () => api.get('/api/my/visas'),
  getMyVisa: (id) => api.get(`/api/my/visas/${encodeURIComponent(id)}`),
  cancelVisa: (id) => api.post(`/api/my/visas/${encodeURIComponent(id)}/cancel`, {}),
  // Employee self-service documents. Upload takes a FormData (file +
  // documentName/docTypeId/expiryDate/reminderDays; optional rowKey to
  // replace an existing one).
  uploadMyDocument: (formData) => uploadWithAuth('/api/my/documents', formData),
  updateMyDocument: (id, data) => api.put(`/api/my/documents/${encodeURIComponent(id)}`, data),
  deleteMyDocument: (id) => api.del(`/api/my/documents/${encodeURIComponent(id)}`),
  submitTravel: (formData) => uploadWithAuth('/api/my/travel', formData),
  listColleagues: () => api.get('/api/shared/users'),
  uploadTravelPassport: (id, formData) =>
    uploadWithAuth(`/api/my/travel/${encodeURIComponent(id)}/passport`, formData),
  listMyTravel: () => api.get('/api/my/travel'),
  getTravel: (id) => api.get(`/api/my/travel/${encodeURIComponent(id)}`),
  // Look up a flight by number (Aviationstack, server-side key) to auto-fill a
  // travel flight preference. Returns { configured, found, airline, from, to, time }.
  lookupFlight: (number) => api.get(`/api/flight/lookup?number=${encodeURIComponent(number)}`),
  getPlannerData: (startDate, endDate) =>
    api.get(`/api/my/planner?startDate=${startDate}&endDate=${endDate}`),
  saveSchedule: (dates, type, note) =>
    api.post('/api/my/schedule', { dates, type, note }),
  deleteScheduleDay: (date) => api.del(`/api/my/schedule/${date}`),
  getCalendarUrl: () => api.get('/api/my/calendar-url'),
  rotateCalendarUrl: () => api.post('/api/my/calendar-url', { rotate: true }),

  // Expiry notifications — items the user has been added as a
  // notifyUserId for that have entered their reminder window.
  listExpiryNotifications: () => api.get('/api/my/expiry-notifications'),
  dismissExpiryNotifications: (rowKeys) =>
    api.post('/api/my/expiry-notifications', { dismiss: rowKeys }),
  markExpiryNotificationsSeen: () =>
    api.post('/api/my/expiry-notifications', { markAllSeen: true }),
};

// Approver helpers
export const approverApi = {
  getQueue: () => api.get('/api/approver/queue'),
  getRequest: (userId, requestId) =>
    api.get(`/api/approver/requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}`),
  approve: (userId, requestId, data) =>
    api.post(`/api/approver/requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}/approve`, data || {}),
  reject: (userId, requestId, data) =>
    api.post(`/api/approver/requests/${encodeURIComponent(userId)}/${encodeURIComponent(requestId)}/reject`, data),

  // Visa approvals
  getVisaQueue: () => api.get('/api/visa-approvals'),
  getVisa: (userId, id) => api.get(`/api/visa-approvals/${encodeURIComponent(userId)}/${encodeURIComponent(id)}`),
  approveVisa: (userId, id, data) => api.post(`/api/visa-approvals/${encodeURIComponent(userId)}/${encodeURIComponent(id)}/approve`, data || {}),
  rejectVisa: (userId, id, data) => api.post(`/api/visa-approvals/${encodeURIComponent(userId)}/${encodeURIComponent(id)}/reject`, data),
};

// ─── Expense Report helpers ───

export const expenseApi = {
  list: () => api.get('/api/my/expenses'),
  get: (reportId) => api.get(`/api/my/expenses/${encodeURIComponent(reportId)}`),
  create: (data) => api.post('/api/my/expenses', data),
  update: (reportId, data) => api.put(`/api/my/expenses/${encodeURIComponent(reportId)}`, data),
  submit: (reportId) => api.post(`/api/my/expenses/${encodeURIComponent(reportId)}/submit`),
  cancel: (reportId) => api.post(`/api/my/expenses/${encodeURIComponent(reportId)}/cancel`),
  remove: (reportId) => api.del(`/api/my/expenses/${encodeURIComponent(reportId)}`),
  saveLineItems: (reportId, items) =>
    api.post(`/api/my/expenses/${encodeURIComponent(reportId)}/items`, { items }),
  uploadReceipt: (reportId, formData) =>
    uploadWithAuth(`/api/my/expenses/${encodeURIComponent(reportId)}/receipts`, formData),
  getReceiptUrl: (reportId, receiptId) =>
    api.get(`/api/my/expenses/${encodeURIComponent(reportId)}/receipts/${encodeURIComponent(receiptId)}`),
  deleteReceipt: (reportId, receiptId) =>
    api.del(`/api/my/expenses/${encodeURIComponent(reportId)}/receipts/${encodeURIComponent(receiptId)}`),
  // Teamwork project list for the expense Project picker. Returns
  // { configured, projects: [{ id, name }], error? }. The Teamwork key
  // is read server-side and never reaches the browser.
  listProjects: () => api.get('/api/my/projects'),
  // Historical exchange rate (from -> SAR) as of `date` (YYYY-MM-DD).
  // Returns { rate, asOf } or { rate: null } if unavailable. Read
  // server-side from a free FX source; the rate field stays editable.
  getFxRate: (from, date) =>
    api.get(`/api/expense/fx-rate?from=${encodeURIComponent(from)}${date ? `&date=${encodeURIComponent(date)}` : ''}`),
};

export const adminExpenseApi = {
  listExpenseTypes: () => api.get('/api/mgmt/expense-types'),
  upsertExpenseType: (typeId, data) =>
    api.post(`/api/mgmt/expense-types/${typeId}`, data),
  deleteExpenseType: (typeId) => api.del(`/api/mgmt/expense-types/${typeId}`),

  // Positions — used by the expense-report Position dropdown. Read is
  // open to any authenticated user; mutations are admin-only.
  listPositions: () => api.get('/api/mgmt/positions'),
  upsertPosition: (positionId, data) => api.post(`/api/mgmt/positions/${positionId}`, data),
  deletePosition: (positionId) => api.del(`/api/mgmt/positions/${positionId}`),

  listReports: (params = {}) =>
    api.get(`/api/mgmt/expense-reports?${new URLSearchParams(params)}`),

  // Bank-Location-based 3% foreign-txn fee recompute for existing
  // draft/pending expense reports. GET previews counts (no writes); POST
  // with confirm:true actually persists the recomputed line items/totals.
  recomputeFees: (confirm = false) =>
    confirm
      ? api.post('/api/mgmt/expense-recompute-fees', { confirm: true })
      : api.get('/api/mgmt/expense-recompute-fees'),
  exportReports: (params = {}) =>
    api.get(`/api/mgmt/expense-reports/export?${new URLSearchParams(params)}`),

  // Server-side bundle: ZIP with XLSX + raw receipts/. Built on the
  // backend so we never have to fetch blob storage cross-origin from
  // the browser (Azurite has no CORS in local dev).
  bundleExpenseReport: async (userId, reportId) => {
    const token = await getAccessToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['X-ProHR-Token'] = `Bearer ${token}`;
    }
    const res = await fetch(
      `/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/bundle`,
      { headers, credentials: 'include' }
    );
    if (!res.ok) {
      const d = await res.json().catch(() => null);
      throw new Error(d?.error || `Bundle export failed: ${res.status}`);
    }
    return res.blob();
  },

  // Admin overrides on a specific expense report (mirror leave-requests).
  getExpenseReport: (userId, reportId) =>
    api.get(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/full`),
  updateExpenseReport: (userId, reportId, updates) =>
    api.post(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}`, { updates }),
  deleteExpenseReport: (userId, reportId) =>
    api.del(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}`),
  restartExpenseChain: (userId, reportId, fromStep = 0) =>
    api.post(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/restart-chain`, { fromStep }),
  // Finance/admin: replace a report's line items (correct amounts, currency,
  // etc.). Server recomputes totals + the 3% fee against the OWNER's home
  // currency and blocks edits on paid reports.
  updateExpenseLineItems: (userId, reportId, items) =>
    api.post(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/line-items`, { items }),
  // Finance/admin: mark an approved report as paid. Server enforces
  // the approved -> paid transition only.
  markPaid: (userId, reportId) =>
    api.post(`/api/mgmt/expense-reports/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/mark-paid`, {}),

  // Monthly expense report (Finance + Admin). month is 'YYYY-MM'; defaults
  // to the current month server-side when omitted.
  monthlyReport: (month) =>
    api.get(`/api/mgmt/expense-reports/monthly-report${month ? `?month=${encodeURIComponent(month)}` : ''}`),
  exportMonthlyReport: async (month, fields) => {
    const token = await getAccessToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['X-ProHR-Token'] = `Bearer ${token}`;
    }
    const params = [];
    if (month) params.push(`month=${encodeURIComponent(month)}`);
    if (Array.isArray(fields) && fields.length > 0) params.push(`fields=${encodeURIComponent(fields.join(','))}`);
    const url = `/api/mgmt/expense-reports/monthly-report-export${params.length ? `?${params.join('&')}` : ''}`;
    const res = await fetch(url, { headers, credentials: 'include' });
    if (!res.ok) {
      const d = await res.json().catch(() => null);
      throw new Error(d?.error || `Export failed: ${res.status}`);
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const fileName = match ? match[1] : `expense-monthly-report-${month || 'current'}.xlsx`;
    const blob = await res.blob();
    return { blob, fileName };
  },

  // Receipt Recovery tool — look up an employee (by email or userId) and
  // surface every receipt across their reports (flagging orphaned ones)
  // plus any stray blobs with no metadata row at all.
  recoverReceipts: (emailOrUserId) => {
    const isEmail = emailOrUserId.includes('@');
    const param = isEmail ? `email=${encodeURIComponent(emailOrUserId)}` : `userId=${encodeURIComponent(emailOrUserId)}`;
    return api.get(`/api/mgmt/expense-recover?${param}`);
  },
  // Restore selected receipts into a fresh draft report owned by the
  // employee. Table Storage partitionKey is immutable, so the backend
  // deletes+recreates each receipt row under the new report id.
  restoreReceipts: ({ userId, receiptRowKeys, fromReportId }) =>
    api.post('/api/mgmt/expense-recover', { userId, receiptRowKeys, fromReportId }),
};

export const expenseApproverApi = {
  getQueue: () => api.get('/api/approver/expense-queue'),
  approve: (userId, reportId, data) =>
    api.post(`/api/approver/expense-requests/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/approve`, data || {}),
  reject: (userId, reportId, data) =>
    api.post(`/api/approver/expense-requests/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}/reject`, data),
  // Read-only history — reports where the caller approved a step. Scoped
  // server-side to the caller's own approvals; see api/approver-expense-history.
  getHistory: () => api.get('/api/approver/expense-history'),
  // Detail for a single history row — reuses the same approver-expense-get
  // route the live review page uses (its access rule now checks the caller's
  // email is a chain participant at all, not just the current pending step).
  getHistoryReport: (userId, reportId) =>
    api.get(`/api/approver/expenses/${encodeURIComponent(userId)}/${encodeURIComponent(reportId)}`),
};
